import { supabase } from "@/integrations/supabase/client";

const EDGE_FUNCTION_URL = 'https://injtcwsaozsjysyjwyot.supabase.co/functions/v1/shopify-products';

// Hidden addon products that should never appear in product listings, search, or grids
const HIDDEN_PRODUCT_HANDLES = new Set([
  'תוספת-אייקון-לחריטה',
]);

/** Filter out hidden addon products from display */
export function filterHiddenProducts<T extends { handle?: string; node?: { handle?: string } }>(items: T[]): T[] {
  return items.filter(item => {
    const handle = item.handle || item.node?.handle;
    return !handle || !HIDDEN_PRODUCT_HANDLES.has(handle);
  });
}

async function shopifyRequest(query: string, variables: any = {}) {
  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.errors) {
      throw new Error(`GraphQL error: ${result.errors.map((e: any) => e.message).join(', ')}`);
    }

    return result;
  } catch (error) {
    console.error('Error in Shopify request:', error);
    throw error;
  }
}

export interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  descriptionHtml?: string;
  handle: string;
  createdAt?: string;
  productType?: string;
  tags?: string[];
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  images: {
    edges: Array<{
      node: {
        url: string;
        altText: string | null;
      };
    }>;
  };
  variants: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        price: {
          amount: string;
          currencyCode: string;
        };
        availableForSale: boolean;
        image?: {
          url: string;
          altText: string | null;
        } | null;
        selectedOptions: Array<{
          name: string;
          value: string;
        }>;
      };
    }>;
  };
  options: Array<{
    name: string;
    values: string[];
  }>;
  collections?: {
    edges: Array<{
      node: {
        id: string;
        handle: string;
        title: string;
      };
    }>;
  };
}

export async function fetchShopifyProducts(first: number = 20, cursor?: string): Promise<{ edges: { node: ShopifyProduct }[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }> {
  const query = `
    query GetProducts($first: Int!, $after: String) {
      products(first: $first, after: $after, sortKey: CREATED_AT, reverse: true) {
        edges {
          node {
            id
            title
            description
            descriptionHtml
            handle
            createdAt
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  image {
                    url
                    altText
                  }
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            options {
              name
              values
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { first, after: cursor });
    return {
      edges: filterHiddenProducts(result.data.products.edges),
      pageInfo: result.data.products.pageInfo
    };
  } catch (error) {
    console.error('Error fetching Shopify products:', error);
    return { edges: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
}

export async function fetchProductsByCollection(collectionHandle: string, first: number = 50, cursor?: string): Promise<{ edges: { node: ShopifyProduct }[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }> {
  const query = `
    query GetCollectionProducts($handle: String!, $first: Int!, $after: String) {
      collection(handle: $handle) {
        products(first: $first, after: $after, sortKey: COLLECTION_DEFAULT) {
          edges {
            node {
              id
              title
            description
            descriptionHtml
            handle
            createdAt
            tags
            priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              images(first: 1) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    availableForSale
                    image {
                      url
                      altText
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
              options {
                name
                values
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { handle: collectionHandle, first, after: cursor });
    return {
      edges: filterHiddenProducts(result.data.collection?.products.edges || []),
      pageInfo: result.data.collection?.products.pageInfo || { hasNextPage: false, endCursor: null }
    };
  } catch (error) {
    console.error('Error fetching collection products:', error);
    return { edges: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
}

export async function fetchProductByHandle(handle: string): Promise<ShopifyProduct | null> {
  const query = `
    query GetProduct($handle: String!) {
      productByHandle(handle: $handle) {
        id
        title
        description
        descriptionHtml
        handle
        productType
        tags
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        images(first: 10) {
          edges {
            node {
              url
              altText
            }
          }
        }
        variants(first: 50) {
          edges {
            node {
              id
              title
              price {
                amount
                currencyCode
              }
              availableForSale
              image {
                url
                altText
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
        options {
          name
          values
        }
        collections(first: 5) {
          edges {
            node {
              id
              handle
              title
            }
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { handle });
    return result.data.productByHandle;
  } catch (error) {
    console.error('Error fetching product:', error);
    return null;
  }
}

export interface LineItemAttribute {
  key: string;
  value: string;
}

export async function createStorefrontCheckout(
  lineItems: Array<{
    variantId: string;
    quantity: number;
    attributes?: LineItemAttribute[];
  }>,
  locale?: string,
  discountCode?: string
) {
  // Build cart input - include discount codes if provided
  const cartInput: {
    lines: Array<{
      merchandiseId: string;
      quantity: number;
      attributes: LineItemAttribute[];
    }>;
    discountCodes?: string[];
  } = {
    lines: lineItems.map(item => ({
      merchandiseId: item.variantId,
      quantity: item.quantity,
      attributes: item.attributes || [],
    })),
  };

  // Add discount code to cart creation if provided
  if (discountCode) {
    cartInput.discountCodes = [discountCode];
  }

  const mutation = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
        }
        userErrors {
          code
          field
          message
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(mutation, {
      input: cartInput,
    });
    
    if (result.data.cartCreate.userErrors && result.data.cartCreate.userErrors.length > 0) {
      throw new Error(result.data.cartCreate.userErrors[0].message);
    }

    const cart = result.data.cartCreate.cart;
    
    // Add channel=online_store parameter to avoid password-protected checkout
    const checkoutUrl = new URL(cart.checkoutUrl);

    // IMPORTANT: Shopify may return the shop's primary (custom) domain in checkoutUrl.
    // If that domain is pointed to this Lovable storefront, checkout will break.
    // Force the permanent Shopify domain to ensure checkout always resolves.
    const PERMANENT_SHOPIFY_DOMAIN = "alice-jewelry-il.myshopify.com";
    const isNonShopifyHost =
      !checkoutUrl.hostname.endsWith(".myshopify.com") &&
      !checkoutUrl.hostname.endsWith("shopify.com");
    if (isNonShopifyHost) {
      checkoutUrl.hostname = PERMANENT_SHOPIFY_DOMAIN;
    }
    
    checkoutUrl.searchParams.set('channel', 'online_store');
    
    // Add locale parameter for multi-language support
    // Only pass Shopify-supported locale codes (2-letter ISO 639-1)
    if (locale) {
      const shortLocale = locale.split('-')[0]; // e.g. 'he-IL' -> 'he'
      checkoutUrl.searchParams.set('locale', shortLocale);
    }

    return {
      id: cart.id,
      webUrl: checkoutUrl.toString(),
    };
  } catch (error) {
    console.error('Error creating checkout:', error);
    throw error;
  }
}

export interface ShopifyCollection {
  node: {
    id: string;
    title: string;
    handle: string;
    description: string;
    image: {
      url: string;
      altText: string | null;
    } | null;
  };
}

export async function fetchShopifyCollections(first: number = 250, after?: string | null): Promise<{ collections: ShopifyCollection[]; hasNextPage: boolean; endCursor: string | null }> {
  const query = `
    query GetCollections($first: Int!, $after: String) {
      collections(first: $first, after: $after) {
        edges {
          node {
            id
            title
            handle
            description
            image {
              url
              altText
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { first, after });
    const collections = result.data.collections.edges;
    const hasNextPage = result.data.collections.pageInfo.hasNextPage;
    const endCursor = result.data.collections.pageInfo.endCursor;

    return { collections, hasNextPage, endCursor };
  } catch (error) {
    console.error('Error fetching Shopify collections:', error);
    return { collections: [], hasNextPage: false, endCursor: null };
  }
}

// Admin-only: fetch ALL collections (including ones not visible to the storefront)
export async function fetchShopifyAdminCollections(
  first: number = 250,
  after?: string | null,
): Promise<{ collections: ShopifyCollection[]; hasNextPage: boolean; endCursor: string | null }> {
  const { data, error } = await supabase.functions.invoke('shopify-admin-collections', {
    body: { first, after: after ?? null },
  });

  if (error) {
    console.error('Error fetching Shopify admin collections:', error);
    throw error;
  }

  // Check for backend error responses
  if (data?.error) {
    console.error('Shopify admin collections error:', data.error);
    throw new Error(data.message || data.error);
  }

  return {
    collections: (data?.collections ?? []) as ShopifyCollection[],
    hasNextPage: Boolean(data?.hasNextPage),
    endCursor: (data?.endCursor ?? null) as string | null,
  };
}
// Fetch related products from the same collections as the given product handles
export async function fetchRelatedProducts(
  productHandles: string[],
  excludeHandles: string[],
  maxProducts: number = 8
): Promise<ShopifyProduct[]> {
  if (productHandles.length === 0) return [];

  try {
    // Step 1: Get collections for each cart product
    const collectionHandles = new Set<string>();
    
    for (const handle of productHandles.slice(0, 4)) { // Limit to first 4 to avoid too many requests
      const product = await fetchProductByHandle(handle);
      if (product?.collections?.edges) {
        for (const edge of product.collections.edges) {
          collectionHandles.add(edge.node.handle);
        }
      }
    }

    if (collectionHandles.size === 0) return [];

    // Step 2: Fetch products from those collections
    const allProducts = new Map<string, ShopifyProduct>();
    const excludeSet = new Set([...excludeHandles, ...Array.from(HIDDEN_PRODUCT_HANDLES)]);

    for (const collHandle of Array.from(collectionHandles).slice(0, 3)) { // Limit collections
      const { edges } = await fetchProductsByCollection(collHandle, 10);
      for (const edge of edges) {
        const p = edge.node;
        if (!excludeSet.has(p.handle) && !allProducts.has(p.id)) {
          allProducts.set(p.id, p);
        }
      }
      if (allProducts.size >= maxProducts) break;
    }

    return Array.from(allProducts.values()).slice(0, maxProducts);
  } catch (error) {
    console.error('Error fetching related products:', error);
    return [];
  }
}

function buildHebrewSearchTerms(input: string): string[] {
  const raw = (input || "").trim().toLowerCase();
  if (!raw) return [];

  const hebrewSuffixes = ["ים", "ות", "ה", "ת", "י", "ו", "ן", "ית"];
  const hebrewPrefixes = ["ה", "ו", "ב", "ל", "מ", "כ", "ש"];

  const normalize = (term: string) => {
    let t = term;
    for (const suffix of hebrewSuffixes) {
      if (t.endsWith(suffix) && t.length > suffix.length + 2) {
        t = t.slice(0, -suffix.length);
        break;
      }
    }
    for (const prefix of hebrewPrefixes) {
      if (t.startsWith(prefix) && t.length > prefix.length + 2) {
        t = t.slice(prefix.length);
        break;
      }
    }
    return t;
  };

  const terms = new Set<string>();
  
  // Split input into individual words
  const words = raw.split(/\s+/).filter(w => w.length >= 2);
  
  for (const word of words) {
    terms.add(word);
    const base = normalize(word);
    if (base && base !== word) terms.add(base);
    
    // Add common plural forms so "טבעת" can match "טבעות" and vice versa
    if (base.length >= 2) {
      terms.add(`${base}ים`);
      terms.add(`${base}ות`);
    }
  }
  
  // Also add the full phrase if it's different from a single word
  if (words.length > 1) {
    terms.add(raw);
    const base = normalize(raw);
    if (base && base !== raw) terms.add(base);
  }

  return Array.from(terms).filter((t) => t.trim().length >= 2);
}

// Check if a product matches multiple search terms (smart scoring)
function scoreProductMatch(product: ShopifyProduct, searchTerms: string[]): number {
  const title = (product.title || "").toLowerCase();
  const description = (product.description || "").toLowerCase();
  const productType = (product.productType || "").toLowerCase();
  const tags = (product.tags || []).map(t => t.toLowerCase()).join(" ");
  const collections = (product.collections?.edges || []).map(e => e.node.title.toLowerCase()).join(" ");
  
  // Combine all searchable text
  const allText = `${title} ${description} ${productType} ${tags} ${collections}`;
  
  let score = 0;
  let termsFound = 0;
  
  for (const term of searchTerms) {
    const termFound = allText.includes(term);
    if (termFound) {
      termsFound++;
      
      // Title match (highest priority)
      if (title.includes(term)) score += 10;
      
      // Tag match
      if (tags.includes(term)) score += 6;
      
      // Product type match
      if (productType.includes(term)) score += 5;
      
      // Collection match
      if (collections.includes(term)) score += 4;
      
      // Description match
      if (description.includes(term)) score += 3;
    }
  }
  
  // Big bonus if ALL search terms are found somewhere in the product
  if (searchTerms.length > 0 && termsFound === searchTerms.length) {
    score += 20 * searchTerms.length; // Bonus for complete match
  } else if (termsFound > 0) {
    // Partial bonus based on percentage of terms found
    score += Math.floor((termsFound / searchTerms.length) * 10);
  }
  
  return score;
}

export interface SearchResult {
  exactMatches: ShopifyProduct[];
  relatedProducts: ShopifyProduct[];
  searchTermsUsed: string[];
  fallbackTerm?: string; // Which term was used for fallback results
}

export async function searchShopifyProducts(
  searchText: string,
  first: number = 40,
): Promise<ShopifyProduct[]> {
  const result = await searchShopifyProductsWithRelated(searchText, first);
  // For backwards compatibility, return combined results
  return [...result.exactMatches, ...result.relatedProducts.filter(
    p => !result.exactMatches.some(e => e.id === p.id)
  )].slice(0, first);
}

export async function searchShopifyProductsWithRelated(
  searchText: string,
  first: number = 40,
): Promise<SearchResult> {
  const terms = buildHebrewSearchTerms(searchText);
  const searchLower = searchText.toLowerCase().trim();
  const originalWords = searchLower.split(/\s+/).filter(w => w.length >= 2);
  
  // Build comprehensive search query that includes:
  // 1. Product title matches
  // 2. Product type matches
  // 3. Tag matches
  // 4. Collection/category name matches (via product_type which often mirrors collection names)
  const titleQueries = terms.length
    ? terms.map((t) => `title:*${t}*`).join(" OR ")
    : `title:*${searchText}*`;
  
  const typeQueries = terms.length
    ? terms.map((t) => `product_type:*${t}*`).join(" OR ")
    : `product_type:*${searchText}*`;
  
  const tagQueries = terms.length
    ? terms.map((t) => `tag:*${t}*`).join(" OR ")
    : `tag:*${searchText}*`;
  
  // Combine all search conditions with OR
  const queryString = `(${titleQueries}) OR (${typeQueries}) OR (${tagQueries})`;

  const query = `
    query SearchProducts($first: Int!, $query: String!) {
      products(first: $first, query: $query) {
        edges {
          node {
            id
            title
            description
            descriptionHtml
            handle
            createdAt
            productType
            tags
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  image {
                    url
                    altText
                  }
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            options {
              name
              values
            }
            collections(first: 5) {
              edges {
                node {
                  id
                  handle
                  title
                }
              }
            }
          }
        }
      }
    }
  `;

  // Query for fetching more products to search in description (Shopify API doesn't support description search directly)
  const allProductsQuery = `
    query GetAllProducts($first: Int!) {
      products(first: $first) {
        edges {
          node {
            id
            title
            description
            descriptionHtml
            handle
            createdAt
            productType
            tags
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  image {
                    url
                    altText
                  }
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            options {
              name
              values
            }
            collections(first: 5) {
              edges {
                node {
                  id
                  handle
                  title
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    // Run main product search and fetch all products for description/collection search in parallel
    const [mainResult, allProductsResult, matchingCollections] = await Promise.all([
      shopifyRequest(query, { first: Math.min(first * 2, 100), query: queryString }),
      shopifyRequest(allProductsQuery, { first: 150 }), // Fetch more products for comprehensive search
      searchShopifyCollections(searchText)
    ]);
    
    const directProducts = (mainResult.data.products.edges ?? []).map((e: any) => e.node).filter((p: ShopifyProduct) => !HIDDEN_PRODUCT_HANDLES.has(p.handle)) as ShopifyProduct[];
    const allProducts = (allProductsResult.data.products.edges ?? []).map((e: any) => e.node).filter((p: ShopifyProduct) => !HIDDEN_PRODUCT_HANDLES.has(p.handle)) as ShopifyProduct[];
    
    // Build a map to collect all products and score them
    const productScores = new Map<string, { product: ShopifyProduct; score: number; matchedAllTerms: boolean }>();
    
    // Helper to check if product matches ALL original words
    const matchesAllWords = (product: ShopifyProduct): boolean => {
      const title = (product.title || "").toLowerCase();
      const description = (product.description || "").toLowerCase();
      const productType = (product.productType || "").toLowerCase();
      const tags = (product.tags || []).map(t => t.toLowerCase()).join(" ");
      const collections = (product.collections?.edges || []).map(e => e.node.title.toLowerCase()).join(" ");
      const allText = `${title} ${description} ${productType} ${tags} ${collections}`;
      
      return originalWords.every(word => allText.includes(word));
    };
    
    // Score direct API matches
    for (const product of directProducts) {
      const score = scoreProductMatch(product, terms);
      const matchedAll = matchesAllWords(product);
      productScores.set(product.id, { product, score: score + 5, matchedAllTerms: matchedAll });
    }
    
    // Score all products (for description/collection matches)
    for (const product of allProducts) {
      if (!productScores.has(product.id)) {
        const score = scoreProductMatch(product, terms);
        if (score > 0) {
          const matchedAll = matchesAllWords(product);
          productScores.set(product.id, { product, score, matchedAllTerms: matchedAll });
        }
      }
    }
    
    // Fetch products from matching collections
    if (matchingCollections.length > 0) {
      const collectionProductPromises = matchingCollections.slice(0, 5).map(async (col) => {
        try {
          const colProducts = await fetchProductsByCollection(col.node.handle, 30);
          return colProducts.edges.map(e => e.node);
        } catch {
          return [];
        }
      });
      
      const collectionProductsArrays = await Promise.all(collectionProductPromises);
      const collectionProducts = collectionProductsArrays.flat();
      
      // Add/update scores for collection products
      for (const product of collectionProducts) {
        const existing = productScores.get(product.id);
        const baseScore = scoreProductMatch(product, terms);
        const collectionBonus = 8;
        const matchedAll = matchesAllWords(product);
        
        if (existing) {
          existing.score = Math.max(existing.score, baseScore + collectionBonus);
          existing.matchedAllTerms = existing.matchedAllTerms || matchedAll;
        } else if (baseScore > 0 || collectionBonus > 0) {
          productScores.set(product.id, { product, score: baseScore + collectionBonus, matchedAllTerms: matchedAll });
        }
      }
    }
    
    // Separate exact matches (matched ALL words) from partial/related matches
    const allScored = Array.from(productScores.values())
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score);
    
    const exactMatches = allScored
      .filter(({ matchedAllTerms }) => matchedAllTerms)
      .map(({ product }) => product)
      .slice(0, first);
    
    const relatedProducts = allScored
      .filter(({ matchedAllTerms }) => !matchedAllTerms)
      .map(({ product }) => product)
      .slice(0, first);
    
    // If no exact matches, find the best fallback term (first word that has results)
    let fallbackTerm: string | undefined;
    if (exactMatches.length === 0 && originalWords.length > 1) {
      // Find which word has the most results
      for (const word of originalWords) {
        const matchCount = relatedProducts.filter(p => {
          const allText = `${p.title} ${p.description} ${(p.tags || []).join(" ")}`.toLowerCase();
          return allText.includes(word);
        }).length;
        if (matchCount > 0) {
          fallbackTerm = word;
          break;
        }
      }
    }
    
    return {
      exactMatches,
      relatedProducts,
      searchTermsUsed: originalWords,
      fallbackTerm
    };
  } catch (error) {
    console.error("Error searching Shopify products:", error);
    return {
      exactMatches: [],
      relatedProducts: [],
      searchTermsUsed: [],
      fallbackTerm: undefined
    };
  }
}

export async function searchShopifyCollections(searchText: string): Promise<ShopifyCollection[]> {
  const terms = buildHebrewSearchTerms(searchText);
  const queryString = terms.length
    ? terms.map((t) => `title:*${t}*`).join(" OR ")
    : `title:*${searchText}*`;

  const query = `
    query SearchCollections($query: String!) {
      collections(first: 250, query: $query) {
        edges {
          node {
            id
            title
            handle
            description
            image {
              url
              altText
            }
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { query: queryString });
    return result.data.collections.edges;
  } catch (error) {
    console.error("Error searching Shopify collections:", error);
    return [];
  }
}

export interface ShopifyArticle {
  node: {
    id: string;
    title: string;
    handle: string;
    excerpt: string;
    content: string;
    publishedAt: string;
    image: {
      url: string;
      altText: string | null;
    } | null;
    blog: {
      title: string;
      handle: string;
    };
    author: {
      name: string;
    };
  };
}

export async function fetchShopifyArticles(first: number = 10): Promise<ShopifyArticle[]> {
  const query = `
    query GetArticles($first: Int!) {
      articles(first: $first, sortKey: PUBLISHED_AT, reverse: true) {
        edges {
          node {
            id
            title
            handle
            excerpt
            content
            publishedAt
            image {
              url
              altText
            }
            blog {
              title
              handle
            }
            author {
              name
            }
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { first });
    return result.data.articles.edges;
  } catch (error) {
    console.error('Error fetching Shopify articles:', error);
    return [];
  }
}

export async function fetchArticleByHandle(blogHandle: string, articleHandle: string): Promise<ShopifyArticle['node'] | null> {
  const query = `
    query GetArticle($blogHandle: String!, $articleHandle: String!) {
      blog(handle: $blogHandle) {
        articleByHandle(handle: $articleHandle) {
          id
          title
          handle
          excerpt
          content
          publishedAt
          image {
            url
            altText
          }
          blog {
            title
            handle
          }
          author {
            name
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { blogHandle, articleHandle });
    return result.data.blog?.articleByHandle || null;
  } catch (error) {
    console.error('Error fetching article:', error);
    return null;
  }
}

// Navigation Menu Types and Functions
export interface ShopifyMenuItem {
  id: string;
  title: string;
  url: string;
  type: string;
  items: ShopifyMenuItem[];
}

export interface ShopifyMenu {
  id: string;
  handle: string;
  title: string;
  items: ShopifyMenuItem[];
}

export async function fetchShopifyMenus(): Promise<ShopifyMenu[]> {
  const query = `
    query GetMenus {
      menu(handle: "main-menu") {
        id
        handle
        title
        items {
          id
          title
          url
          type
          items {
            id
            title
            url
            type
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, {});
    const menu = result.data?.menu;
    return menu ? [menu] : [];
  } catch (error) {
    console.error('Error fetching Shopify menus:', error);
    return [];
  }
}

export async function fetchShopifyMenuByHandle(handle: string): Promise<ShopifyMenu | null> {
  const query = `
    query GetMenu($handle: String!) {
      menu(handle: $handle) {
        id
        handle
        title
        items {
          id
          title
          url
          type
          items {
            id
            title
            url
            type
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { handle });
    return result.data?.menu || null;
  } catch (error) {
    console.error('Error fetching Shopify menu:', error);
    return null;
  }
}
